import React, { useState, useEffect } from 'react';
import {
  User,
  LogOut,
  Bell
} from 'lucide-react';
import notificationApi from '../../../api/notificationApi';
import { useSocket } from '../../../context/SocketContext';
import { USER_ROLES } from '../../../utils/constants';
import SidebarItem from './SidebarItem';
import Alert from '../Alert/Alert';
import { allNavigationItems } from '../../../config/navigationItems';
import { hasPermission as checkPermission, hasHRMSPermission, hasCRMPermission, isEmployee } from '../../../utils/navigationUtils';

const DynamicSidebar = ({ userRole, user, onNavigate, activeItem, onLogout, expandedItems, onToggleExpanded }) => {
  const [showLogoutAlert, setShowLogoutAlert] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const socket = useSocket();

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await notificationApi.getNotifications();
        if (response.status === 'success') {
          setUnreadCount(response.data.filter(n => !n.isRead).length);
        }
      } catch (error) {
      }
    };

    fetchUnreadCount();

    if (socket) {
      const handleNewNotification = () => {
        setUnreadCount(prev => prev + 1);
      };
      const handleNotificationRead = () => {
        setUnreadCount(prev => Math.max(0, prev - 1));
      };
      const handleAllNotificationsRead = () => {
        setUnreadCount(0);
      };

      socket.on('new-notification', handleNewNotification);
      socket.on('notification-read', handleNotificationRead);
      socket.on('notifications-all-read', handleAllNotificationsRead);

      return () => {
        socket.off('new-notification', handleNewNotification);
        socket.off('notification-read', handleNotificationRead);
        socket.off('notifications-all-read', handleAllNotificationsRead);
      };
    }
  }, [socket]);

  // Get user from localStorage as fallback if context user doesn't have permissions
  const getUserWithPermissions = () => {
    if (user && user.granularPermissions) {
      return user;
    }

    // Try to get from localStorage
    try {
      const userData = localStorage.getItem('user_data');
      if (userData) {
        const parsedUser = JSON.parse(userData);
        if (parsedUser && parsedUser.granularPermissions) {
          return parsedUser;
        }
      }
    } catch (e) {
    }

    return user;
  };

  const userWithPermissions = getUserWithPermissions();

  // Debug: Log user data when it changes
  useEffect(() => {
  }, [user, userRole, userWithPermissions]);

  // Helper function to check if user has permission
  const hasPermission = (permissionKey) => {
    return checkPermission(userWithPermissions, userRole, permissionKey);
  };

  // Helper function to check if user has any HRMS-related permissions
  const hrmsPermissionCheck = () => {
    return hasHRMSPermission(userWithPermissions, userRole);
  };

  // Helper function to check if user has any CRM-related permissions
  const crmPermissionCheck = () => {
    return hasCRMPermission(userWithPermissions, userRole);
  };

  // Helper function to check if user has HRMS OR CRM permissions
  // Used to gate System Management — visible to any admin-level user
  const systemPermissionCheck = () => {
    return hrmsPermissionCheck() || crmPermissionCheck();
  };

  const employeeCheck = () => {
    return isEmployee(userWithPermissions, userRole);
  };

  const toggleExpanded = (item) => {
    onToggleExpanded(item);
  };

  // Force re-render when activeItem changes
  useEffect(() => {
    setForceUpdate(prev => prev + 1);
  }, [activeItem]);

  const handleLogoutClick = () => {
    setShowLogoutAlert(true);
  };

  const handleLogoutConfirm = () => {
    onLogout();
  };

  // helper functions replaced by utility imports above

  // Filter navigation items based on permissions OR roles
  const getVisibleItems = () => {
    return Object.values(allNavigationItems).filter(item => {
      // 1. Permanent items (Everyone can see)
      if (item.id === 'notifications') {
        return true;
      }

      // 2. Dashboard - special handling
      if (item.id === 'dashboard') {
        if (employeeCheck()) {
          return false; // Move to 'My Services' for pure employees
        }
        return hasPermission('dashboard');
      }

      // 3. My Services - permission based check handled by item.permissionKey below

      // 4. Permission-based checks (Strictly authoritative if permissionKey exists)
      if (item.permissionKey) {
        return hasPermission(item.permissionKey);
      }

      if (item.checkHRMSPermission && hrmsPermissionCheck()) {
        return true;
      }
      if (item.checkCRMPermission && crmPermissionCheck()) {
        return true;
      }
      if (item.checkSystemPermission && systemPermissionCheck()) {
        return true;
      }

      // 5. Role-based fallback (Only if no permissionKey exists)
      if (Array.isArray(item.roles) && item.roles.length > 0) {
        return item.roles.includes(userRole);
      }

      // Default: don't show
      return false;
    });
  };

  // Filter sub-items based on permissions OR roles
  const getVisibleSubItems = (subItems, parentItem = null) => {
    if (!subItems) return [];

    return Object.values(subItems).filter(subItem => {
      // 1. My Services - Dashboard visible for all authenticated users
      if (subItem.id === 'employee_dashboard' && parentItem?.id === 'employee_self_service') {
        return true;
      }

      // 2. Super Admin specific exclusions
      if (subItem.id === 'attendance_dashboard' && userRole === USER_ROLES.SUPER_ADMIN) {
        return false;
      }

      // 3. Permission-based checks (Strictly authoritative if permissionKey exists)
      if (subItem.permissionKey) {
        return hasPermission(subItem.permissionKey);
      }

      if (subItem.checkCRMPermission && crmPermissionCheck()) {
        return true;
      }

      if (subItem.checkHRMSPermission && hrmsPermissionCheck()) {
        return true;
      }

      // 4. Role-based fallback (Only if no permissionKey exists)
      if (Array.isArray(subItem.roles) && subItem.roles.length > 0) {
        return subItem.roles.includes(userRole);
      }

      return false;
    });
  };

  // Filter nested sub-items based on permissions OR roles
  const getVisibleNestedSubItems = (nestedSubItems, parentSubItem = null) => {
    if (!nestedSubItems) return [];

    return Object.values(nestedSubItems).filter(nestedSubItem => {
      // 1. Permission-based check (Strictly authoritative if permissionKey exists)
      if (nestedSubItem.permissionKey) {
        return hasPermission(nestedSubItem.permissionKey);
      }

      // 2. Inheritance from parent submodule permission
      if (parentSubItem && parentSubItem.permissionKey && hasPermission(parentSubItem.permissionKey)) {
        // Allow if role matches, OR if no role restriction is specified
        if (!nestedSubItem.roles || nestedSubItem.roles.length === 0 || nestedSubItem.roles.includes(userRole)) {
          return true;
        }
        
        return false; 
      }

      // 3. Role-based fallback (Only if no permissionKey exists)
      if (Array.isArray(nestedSubItem.roles) && nestedSubItem.roles.length > 0) {
        return nestedSubItem.roles.includes(userRole);
      }

      return false;
    });
  };

  const renderNavigationItem = (item) => {
    const isExpanded = expandedItems[item.id];
    const isActive = activeItem === item.id ||
      (item.subItems && getVisibleSubItems(item.subItems, item).some(subItem =>
        activeItem === subItem.id ||
        (subItem.subItems && getVisibleNestedSubItems(subItem.subItems, subItem).some(nestedSubItem =>
          activeItem === nestedSubItem.id
        ))
      ));

    // Debug logging



    return (
      <div key={item.id} className="mb-1">
        <SidebarItem
          key={`${item.id}-${forceUpdate}`}
          icon={item.icon}
          label={item.label}
          isActive={isActive}
          hasSubItems={item.hasSubItems}
          isExpanded={isExpanded}
          badge={item.id === 'notifications' && unreadCount > 0 ? unreadCount : null}
          onToggle={() => toggleExpanded(item.id)}
          onClick={() => onNavigate(item.id)}
        />

        {item.hasSubItems && isExpanded && (
          <div className="ml-6 mt-1 space-y-1">
            {getVisibleSubItems(item.subItems, item).map(subItem => {
              const isSubItemExpanded = expandedItems[subItem.id];
              const isSubItemActive = activeItem === subItem.id ||
                (subItem.subItems && getVisibleNestedSubItems(subItem.subItems, subItem).some(nestedSubItem =>
                  activeItem === nestedSubItem.id
                ));

              // Only redirect audit_logs to analytics_audit_logs if parent is NOT compliance_policy
              // If parent is compliance_policy, keep it as audit_logs
              const getNavigationTarget = () => {
                if (subItem.id === 'audit_logs' && item.id !== 'compliance_policy') {
                  return 'analytics_audit_logs';
                }
                return subItem.id;
              };

              return (
                <div key={subItem.id}>
                  <SidebarItem
                    key={`${subItem.id}-${forceUpdate}`}
                    icon={subItem.icon}
                    label={subItem.label}
                    isActive={isSubItemActive}
                    hasSubItems={subItem.hasSubItems}
                    isExpanded={isSubItemExpanded}
                    onToggle={() => toggleExpanded(subItem.id)}
                    onClick={() => {
                      // Store parent context for items that exist in multiple places
                      if (getNavigationTarget() === 'candidate_profile_management' ||
                        getNavigationTarget() === 'offer_communication_logs') {
                        sessionStorage.setItem('navContext', item.id);
                      }
                      // Store context for background verification
                      if (getNavigationTarget() === 'background_verification' || 
                          getNavigationTarget() === 'onboarding') {
                        sessionStorage.setItem('bgVerificationNavContext', subItem.id);
                      }
                      // Store context for lead detail navigation
                      if (getNavigationTarget() === 'lead_dashboard' || 
                          getNavigationTarget() === 'lead_list_filters' ||
                          getNavigationTarget() === 'lead_scoring_qualification') {
                        sessionStorage.setItem('leadNavContext', subItem.id);
                      }
                      onNavigate(getNavigationTarget());
                    }}
                    className="text-sm"
                  />

                  {subItem.hasSubItems && isSubItemExpanded && (
                    <div className="ml-6 mt-1 space-y-1">
                      {getVisibleNestedSubItems(subItem.subItems, subItem).map(nestedSubItem => (
                        <SidebarItem
                          key={`${nestedSubItem.id}-${forceUpdate}`}
                          icon={nestedSubItem.icon}
                          label={nestedSubItem.label}
                          isActive={activeItem === nestedSubItem.id}
                          onClick={() => onNavigate(nestedSubItem.id)}
                          className="text-xs"
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Logo Section */}
      <div className="flex-shrink-0 px-4 sm:px-6 py-3 border-b border-gray-200 h-16 flex items-center">
        <div className="flex items-center">
          <div className="w-10 h-10 flex items-center justify-center overflow-hidden">
            <img src="/logo.png" alt="Company Logo" className="w-full h-full object-contain" />
          </div>
          <div className="ml-3">
            <h1 className="text-lg font-bold text-gray-900 tracking-tight">{process.env.REACT_APP_COMPANY_SHORT_NAME}</h1>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 hidden sm:block font-medium">Business Platform</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 p-3 sm:p-4 space-y-2 overflow-y-auto overflow-x-hidden">
        {getVisibleItems().map(renderNavigationItem)}
      </div>

      {/* User Section */}
      <div className="flex-shrink-0 p-3 sm:p-4 border-t border-gray-200">
        <div className="space-y-2">
          <SidebarItem
            icon={User}
            label="Profile"
            onClick={() => onNavigate('profile')}
            isActive={activeItem === 'profile'}
          />
          <SidebarItem
            icon={LogOut}
            label="Logout"
            onClick={handleLogoutClick}
          />
        </div>
      </div>

      {/* Logout Confirmation Alert */}
      <Alert
        isOpen={showLogoutAlert}
        onClose={() => setShowLogoutAlert(false)}
        title="Confirm Logout"
        message="Are you sure you want to logout?"
        type="warning"
        showCancel={true}
        confirmText="YES"
        cancelText="NO"
        onConfirm={handleLogoutConfirm}
        onCancel={() => setShowLogoutAlert(false)}
      />
    </div>
  );
};

export default DynamicSidebar;
